import { Component, Input, OnInit } from "@angular/core";
import { Store } from "@ngrx/store";
import { LoanType } from "app/modules/desking/models/enums";
import * as fromScenarios from "app/modules/desking/scenarios/store";
import { RefreshPencils } from "app/modules/forms/components/forms-print/refresh-pencil-actions";
import { FormAssignment } from "app/modules/forms/models/form-assignment";
import { FormPacket } from "app/modules/forms/models/form-packet";
import { FormAssignmentService } from "app/modules/forms/services/form-assignment.service";
import { FormPacketService } from "app/modules/forms/services/form-packet.service";
import { FormService } from "app/modules/forms/services/form.service";
import { Message, MessageType, SeverityType } from "app/notifications/message";
import { NotificationsService } from "app/notifications/notifications.module";
import { WindowRef } from "app/shared/services/windowRef.provider";

@Component({
  // tslint:disable-next-line:component-selector
  selector: "xrm-desking-forms-print",
  templateUrl: "./forms-print.component.html",
  styleUrls: ["./forms-print.component.scss"],
  providers: [WindowRef],
})
export class FormsPrintComponent implements OnInit {
  private _configId: number;
  
  assignments: { assignment: FormAssignment; isChecked: boolean }[] = [];
  isSelect = true;
  packets: FormPacket[] = [];
  selectedPacketId: number;
  expandedPanel: ExpansionPanel = ExpansionPanel.None;

  @Input()
  showQuickPrint: boolean;

  @Input()
  set configId(value: number) {
    this._configId = value;
  }

  get configId(): number {
    return this._configId;
  }

  @Input()
  paymentId: number;
  @Input()
  loanType: LoanType;
  @Input()
  isPacketsExpanded: boolean;
  // Needed to access the enum inside the template
  expansionPanel = ExpansionPanel;

  isLoadingForms = true;
  isLoadingPackets = true;

  constructor(
    private formService: FormService,
    private assignmentService: FormAssignmentService,
    private packetService: FormPacketService,
    private notificationsService: NotificationsService,
    private store: Store<fromScenarios.State>,
    private windowRef: WindowRef
  ) { }

  ngOnInit() {
    this.loadFormsAndPackets();
  }

  isPrintButtonDisabled() {
    return (
      this.expandedPanel === ExpansionPanel.None ||
      (this.expandedPanel === ExpansionPanel.Forms &&
        this.assignments.filter((assignment) => assignment.isChecked).length ===
        0) ||
      (this.expandedPanel === ExpansionPanel.Packets && !this.selectedPacketId)
    );
  }

  // mat-expansion-panel flash on initail load issue due to angular 7
  fixPanelFlash(index: string) {
    const that = this;
    that.windowRef.nativeWindow.setTimeout(() => {
      if (index === "1") {
        that.isLoadingForms = false;
      } else {
        this.isLoadingPackets = false;
      }
    }, 200);
  }

  onFormCheckChange() {
    let isAllSelected = true;
    let isAllDeselected = true;

    for (let i = 0; i < this.assignments.length; i++) {
      if (this.assignments[i].isChecked) {
        isAllDeselected = false;
      } else {
        isAllSelected = false;
      }

      if (!isAllSelected && !isAllDeselected) {
        break;
      }
    }

    if (
      (this.isSelect && isAllSelected) ||
      (!this.isSelect && isAllDeselected)
    ) {
      this.isSelect = !this.isSelect;
    }
  }

  onPrintForms(eventId: number) {
    if (this.expandedPanel === ExpansionPanel.Forms) {
      const selectedForms = this.assignments
        .filter((assignment) => assignment.isChecked)
        .map((assignment) => assignment.assignment.formId);

      if (selectedForms.length > 0) {
        this.isLoadingForms = true;
        this.formService
          .getStampedFormFiles(
            this.loanType,
            this.paymentId,
            eventId,
            selectedForms
          )
          .subscribe(
            (data: any) => {
              this.downloadFile(data);
              this.fixPanelFlash("1");
              this.store.dispatch(new RefreshPencils());
            },
            (err: any) => {
              this.handleErrorResponse(
                err,
                "Error while downloading the form."
              );
              this.fixPanelFlash("1");
            }
          );
      }
    } else if (this.expandedPanel === ExpansionPanel.Packets) {
      if (this.selectedPacketId) {
        this.isLoadingPackets = true;
        this.packetService
          .getStampedPacketFiles(
            this.selectedPacketId,
            this.loanType,
            eventId,
            this.paymentId
          )
          .subscribe(
            (data: any) => {
              this.downloadFile(data);
              this.fixPanelFlash("0");
            },
            (err: any) => {
              this.handleErrorResponse(
                err,
                "Error while downloading the packet."
              );
              this.fixPanelFlash("0");
            }
          );
      }
    }
  }

  onSelectAllForms() {
    for (let i = 0; i < this.assignments.length; i++) {
      this.assignments[i].isChecked = this.isSelect;
    }
    this.isSelect = !this.isSelect;
  }

  downloadFile(data: any) {
    const blob = new Blob([data], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);
    window.open(url);
  }

  handleErrorResponse(err: any, errorMsgPrefix?: string) {
    const reader = new FileReader();
    reader.onload = (e) => {
      errorMsgPrefix = errorMsgPrefix || "Error while perfoming operation.";
      this.notificationsService.addNotification({
        type: MessageType.Toast,
        severity: SeverityType.Error,
        title: err.statusText || "Error",
        message: `${errorMsgPrefix} - ${(<any>e.target).result}`,
      } as Message);
    };
    reader.readAsText(err.error, "utf-8");
  }

  onOpenedPanel(panel: ExpansionPanel) {
    this.expandedPanel = panel;
  }

  onClosedPanel(panel: ExpansionPanel) {
    if (this.expandedPanel === panel) {
      this.expandedPanel = ExpansionPanel.None;
    }
  }

  private loadFormsAndPackets(): void {
    this.expandedPanel = this.isPacketsExpanded
      ? ExpansionPanel.Packets
      : ExpansionPanel.Forms;
    this.assignmentService
      .getActives(this.configId)
      .subscribe((assignments: FormAssignment[]) => {
        this.assignments = assignments.map((assgn) => {
          return { assignment: assgn, isChecked: false };
        });
        this.fixPanelFlash("1");
      });

    this.packetService
      .getActives(this.configId)
      .subscribe((packets: FormPacket[]) => {
        this.packets = packets;
        this.fixPanelFlash("0");
      });
  }
}

export enum ExpansionPanel {
  None = 0,
  Forms = 1,
  Packets = 2,
}
